import BreadCrumb from "@/components/Shared/BreadCrumb";
import FooterWave2Icon from "@/public/site-assets/svg/footer-wave-2.svg";
import clsx from "classnames";
import { motion } from "framer-motion";
import debounce from "lodash.debounce";
import { useEffect, useRef, useState } from "react";
import { Container } from "reactstrap";
import Buttons from "ui/Buttons/component";
import styles from "./styles.module.scss";
import BrushIcon from "/public/site-assets/svg/brush.svg";
import LeafIcon from "/public/site-assets/svg/leaf.svg";
import LightBlulbIcon from "/public/site-assets/svg/light-blub.svg";
import OverlayDots from "/public/site-assets/svg/overlay-dots.svg";
import PotIcon from "/public/site-assets/svg/pot.svg";
import RulerIcon from "/public/site-assets/svg/ruler.svg";

export const Banner = ({
  bgColor,
  content,
  heading,
  buttons,
  breadcrumb_title,
  secondary_breadcrumb_url,
  secondary_breadcrumb_title,
  backgroundImage,
  showWave,
  waveDirection,
  waveColor,
  waveBackground,
  showTopWave,
  topWaveDirection,
  topWaveColor,
  topWaveBackground,
}) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // Adjust threshold as needed, make it smaller for more sensitive detection
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    observer.observe(sectionRef.current);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileDevice = window.innerWidth <= 991; // Adjust threshold for mobile devices
      // setIsVisible(!mobileDevice); // Hide motion on mobile devices
    };

    handleResize(); // Call initially
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [headerHeight, setHeaderHeight] = useState(0);

  const updateHeaderHeight = debounce(() => {
    const headerElement = document.getElementById("header");
    if (headerElement) {
      setHeaderHeight(headerElement.getBoundingClientRect().height);
    }
  }, 200); // Adjust debounce time as needed

  useEffect(() => {
    // Initial call with a slight delay to ensure element is fully rendered
    setTimeout(updateHeaderHeight, 50);

    // Event listener for window resize
    window.addEventListener("resize", updateHeaderHeight);

    // Cleanup function
    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  return (
    <>
      {showTopWave && (
        <div
          className={clsx(
            "footer",
            topWaveDirection === "down" && "rotate",
            topWaveColor,
            topWaveBackground,
          )}
          style={{ marginBottom: "-1px", zIndex: 1, position: "relative" }}
        >
          <FooterWave2Icon />
        </div>
      )}
      <section
        ref={sectionRef}
        className={clsx(bgColor, styles.root, "sub-banner", { "blog-page": !!backgroundImage })}
        style={{
          marginTop: `${headerHeight}px`,
        }}
      >
        <img
          src={backgroundImage?.url}
          alt=""
          loading="lazy"
          className="overlay-img position-absolute"
          width="100%"
          height="100%"
        ></img>
        <Container className="position-relative">
          <motion.span
            className="floating-icons"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1.5, delay: 0.3 }}
          >
            <LightBlulbIcon />
          </motion.span>
          <motion.span
            className="floating-icons"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1.5, delay: 2.3 }}
          >
            <BrushIcon />
          </motion.span>
          <motion.span
            className="floating-icons"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1.5, delay: 1.3 }}
          >
            <LeafIcon />
          </motion.span>
          <motion.span
            className="floating-icons"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1.5, delay: 2 }}
          >
            <PotIcon />
          </motion.span>
          <motion.span
            className="floating-icons"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            transition={{ duration: 1.5, delay: 1 }}
          >
            <RulerIcon />
          </motion.span>

          <div className={`position-relative wrapper pb-2`}>
            <div>
              <BreadCrumb
                title={
                  <>
                    {secondary_breadcrumb_url ? (
                      <span>
                        <a href={secondary_breadcrumb_url}>{secondary_breadcrumb_title}</a>
                      </span>
                    ) : (
                      ""
                    )}
                    <span className="active">{breadcrumb_title}</span>
                  </>
                }
                className={`py-2`}
              />
              {heading && (
                <motion.div
                  className={`position-relative`}
                  initial={{ opacity: 0, y: 50 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5 }}
                >
                  <h1 className="mb-3 mt-3">{heading}</h1>
                </motion.div>
              )}
              {content && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                </motion.div>
              )}
              {buttons && (
                <Buttons buttons={buttons} className="d-flex align-items-center text-center" />
              )}
            </div>
          </div>

          <motion.div
            className="overlay-dots"
            initial={{ opacity: 0, y: 50 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.5 }}
          >
            <OverlayDots />
          </motion.div>
          <motion.div
            className="overlay-dots"
            initial={{ opacity: 0, y: -50 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.5 }}
          >
            <OverlayDots />
          </motion.div>
        </Container>
        {showWave && (
          <div
            className={clsx(
              "footer",
              waveDirection === "down" && "rotate",
              waveColor,
              waveBackground,
            )}
            style={{ marginTop: "-1px" }}
          >
            <FooterWave2Icon />
          </div>
        )}
      </section>
    </>
  );
};

export default Banner;
